import React, {
  useMemo, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import DragDropContextComponent from '../DragAndDrop/DragDropContext';
import { useDialogs, useToasts } from '../../hooks';
import Button from '../Button/Button';
import { Plus } from '../Icons/Icons';
import { reorderCustomFields } from '../../utils/api/modelForm';
import CustomFieldsAddEditModal from './CustomFieldsAddEditModal';
import CustomFieldItem from './CustomFieldItem';
import { useUrlModals } from '../../utils/useUrlModals';

const CustomFieldsDisplay = ({
  orgId, activeTabName, fetchCustomFields, formItems, setFormItems, initialFormItems, modelId, error = null, setInitialFormItems,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [numberValue, setNumberValue] = useState('');

  const { openDialog, closeDialog } = useDialogs();
  const { showToast } = useToasts();

  // Centralize all modal URL state
  const { modals, openModal, closeModal } = useUrlModals({
    // which form is being edited (used to scope the add/edit modal)
    form: { isIdParam: true },
    // open "create" modal (no id)
    addCustomField: { isIdParam: false },
    // open "edit" modal (id of the field)
    editCustomField: { isIdParam: true },
  });

  // Form items transformed for render
  const customFieldsData = useMemo(() => (
    Array.isArray(formItems)
      ? formItems.map(item => ({
        id: item.id,
        label: item.label,
        type: item.type,
        options: item.options,
        required: Boolean(item.required),
        active: Boolean(item.active),
        encrypt: Boolean(item.encrypt),
        position: item.position,
        fieldCount: item.fields_count || 0,
      }))
      : []
  ), [formItems]);

  // Load form fields when orgId/activeTabName changes
  useEffect(() => {
    if (orgId) fetchCustomFields();
  }, [orgId, activeTabName, fetchCustomFields]);

  const getIconClass = (type) => {
    if (type === 'FormItem::Heading') return 'ml-4 mb-1';
    if (type === 'FormItem::Multiselect') return 'ml-4 flex items-center justify-center';
    return 'ml-4 flex items-center justify-center';
  };

  const handleOpenCreateModal = () => {
    openModal('form', activeTabName);
    openModal('addCustomField');
    closeModal('editCustomField');
  };

  const handleEditClick = (field) => {
    openModal('form', activeTabName);
    openModal('editCustomField', field.id);
    closeModal('addCustomField');
  };

  const handleCloseModal = () => {
    closeModal('addCustomField');
    closeModal('editCustomField');
    closeModal('form');
  };

  // Drag & drop reorder
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const updatedItems = [...formItems];
    const [movedItem] = updatedItems.splice(result.source.index, 1);
    updatedItems.splice(result.destination.index, 0, movedItem);

    const reindexedItems = updatedItems.map((item, index) => ({
      ...item,
      position: index,
    }));

    setFormItems(reindexedItems);
  };

  const hasOrderChanged = useMemo(() => {
    if (formItems.length !== initialFormItems.length) return false;
    return formItems.some((item, index) => item.id !== initialFormItems[index]?.id);
  }, [formItems, initialFormItems]);

  // Confirm "Save order..."
  const handleOpenSaveConfirm = () => {
    openDialog({
      message: 'This will reorder the custom fields for everyone in the organization. Are you sure you want to save these changes?',
      show: true,
      actions: [
        {
          label: 'Cancel',
          variant: 'secondary',
          onClick: closeDialog,
        },
        {
          label: 'Reorder fields',
          onClick: async () => {
            try {
              await reorderCustomFields(activeTabName, orgId, formItems);
              setInitialFormItems(formItems);
              showToast({ message: 'Custom fields have been successfully reordered.', type: 'success' });
            } catch (err) {
              showToast({ message: err.message, type: 'error' });
            }
            closeDialog();
          },
        },
      ],
    });
  };

  // Determine if the modal should be shown for this tab,
  // and which field (if any) is being edited.
  const isThisFormActiveInUrl = modals.form?.value === activeTabName;
  const isAddOpen = isThisFormActiveInUrl && modals.addCustomField?.isOpen;
  const isEditOpen = isThisFormActiveInUrl && modals.editCustomField?.isOpen;

  const fieldIdToEdit = isEditOpen ? Number(modals.editCustomField.value) : null;
  const fieldToEdit = useMemo(
    () => (fieldIdToEdit ? customFieldsData.find(f => f.id === fieldIdToEdit) || null : null),
    [fieldIdToEdit, customFieldsData],
  );
  const isReallyEditOpen = isEditOpen && !!fieldToEdit;
  const isModalOpen = isAddOpen || isReallyEditOpen;

  return (
    <>
      {error && <p className="text-vamos-red">{error}</p>}

      {customFieldsData.length === 0 ? (
        <p className="h-5.75">Add your first custom field.</p>
      ) : (
        <DragDropContextComponent
          draggableItems={customFieldsData.map((field, index) => (
            <CustomFieldItem
              key={field.id}
              field={field}
              getIconClass={getIconClass}
              handleEditClick={handleEditClick}
              setSelectedDate={setSelectedDate}
              numberValue={numberValue}
              setNumberValue={setNumberValue}
              draggableIndex={index}
            />
          ))}
          onDragEnd={handleDragEnd}
          direction="vertical"
          className="flex flex-col"
        />
      )}

      <div className="flex justify-between items-center mt-6.5">
        {/* Add Custom Field button */}
        <Button
          label="Custom Field"
          size="md"
          Icon={Plus}
          className="font-medium gap-2.5 px-4.5"
          onClick={handleOpenCreateModal}
          dataTestId="add-custom-field-button"
        />

        {/* Add/Edit Modal (URL-driven) */}
        {isModalOpen && (
          <CustomFieldsAddEditModal
            orgId={String(orgId)}
            modelId={String(modelId)}
            activeTabName={activeTabName}
            handleClose={handleCloseModal}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            numberValue={numberValue}
            setNumberValue={setNumberValue}
            formItems={formItems}
            setFormItems={setFormItems}
            fieldData={isAddOpen ? null : fieldToEdit}// null for "add", object for "edit"
            setInitialFormItems={setInitialFormItems}
          />
        )}

        {/* Cancel and Save order button */}
        {formItems.length > 0 && (
          <div className="flex gap-4">
            <Button
              label="Save order..."
              size="md"
              isDisabled={!hasOrderChanged}
              onClick={handleOpenSaveConfirm}
              className={`font-medium gap-2.5 px-4.5 ${
                !hasOrderChanged ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            />
          </div>
        )}
      </div>
    </>
  );
};

CustomFieldsDisplay.propTypes = {
  orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  activeTabName: PropTypes.string.isRequired,
  fetchCustomFields: PropTypes.func.isRequired,
  formItems: PropTypes.array.isRequired,
  setFormItems: PropTypes.func.isRequired,
  initialFormItems: PropTypes.array.isRequired,
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  error: PropTypes.string,
  setInitialFormItems: PropTypes.func.isRequired,
};

export default CustomFieldsDisplay;
